import { useState, useEffect, useCallback } from 'react';
import { getApiErrorMessage } from '../../utils/api/getApiErrorMessage';
import { dedupeByKey } from '../../utils/collections/dedupeByKey';
import { normalizeHiringRow } from '../../utils/hiring/normalizeHiringRow';

/**
 * Hook to fetch hired employees for a specific job
 * @param {number} jobId - Job/JobOffer ID
 * @param {boolean} enabled - Whether to fetch (e.g., when tab is active)
 * @returns {Object} { hiredEmployees, loading, error, refresh }
 */
const useFetchJobHirings = (jobId, enabled = true) => {
  const [hiredEmployees, setHiredEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchHiredEmployees = useCallback(async (signal) => {
    if (!enabled || !jobId) {
      setHiredEmployees([]);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const url = `${process.env.REACT_APP_BACKEND_STAGING_URL}/api/hirings/job/${jobId}`;
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        signal
      });

      if (!response.ok) {
        const detail = await getApiErrorMessage(response);
        throw new Error(detail || `Failed to fetch hired employees (HTTP ${response.status})`);
      }

      const apiResponse = await response.json();
      const rows = Array.isArray(apiResponse?.data) ? apiResponse.data : [];
      const normalized = rows.map(normalizeHiringRow);

      console.log('normalized hired employees', normalized)

      setHiredEmployees(dedupeByKey(normalized, (row) => row?.employeeId));
    } catch (err) {
      // Ignore aborts (navigation/unmount)
      if (err?.name === 'AbortError') return;
      setError(err instanceof Error ? err : new Error('Failed to fetch hired employees'));
      setHiredEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [jobId, enabled]);

  useEffect(() => {
    const controller = new AbortController();
    fetchHiredEmployees(controller.signal);
    return () => controller.abort();
  }, [fetchHiredEmployees]);

  return {
    hiredEmployees,
    loading,
    error,
    refresh: () => {
      const controller = new AbortController();
      fetchHiredEmployees(controller.signal);
      return () => controller.abort();
    }
  };
};

export default useFetchJobHirings;

